import { Box, Drawer } from '@mui/material'
import { ModalContextProvider } from 'components/no-ui/context/modal-provider'
import { DisplayModeContext } from 'lib/context/app'
import { useRouter } from 'next/router'
import { createContext, ReactNode, useCallback, useMemo, useState, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'

export type DrawerProviderProps = {
    children: ReactNode
}

export type DrawerVariants = 'sm' | 'md' | 'lg'

export type DrawerOptions = {
    variant?: DrawerVariants
    padding?: boolean
    onClose?: () => void
}

export type DrawerProviderContext = [
    (content: ReactNode, options?: DrawerOptions) => void, // Open drawer
    () => void, // Close drawer
    (canClose: boolean) => void // set canClose
]

const DrawerContextDefaultValue: DrawerProviderContext = [
    (): void => undefined,
    (): void => undefined,
    (): void => undefined,
]

export const DrawerContext = createContext<DrawerProviderContext>(DrawerContextDefaultValue)

const widths = {
    sm: 'min(calc(100vw - 10rem), 42rem)',
    md: 'min(calc(100vw - 10rem), 64rem)',
    lg: 'min(calc(100vw - 10rem), 96rem)',
}

export const DrawerProvider = ({ children }: DrawerProviderProps): React.ReactElement => {
    const intl = useIntl()
    const router = useRouter()
    const onCloseRef = useRef<(() => void) | undefined>(undefined)

    const [drawerContent, setDrawerContent] = useState<ReactNode>(null)
    const [drawerOptions, setDrawerOptions] = useState<DrawerOptions | undefined>(undefined)
    const [isOpen, setIsOpen] = useState(false)
    const [canClose, setCanClose] = useState(true)

    const variant = drawerOptions?.variant || 'sm'
    const padding = drawerOptions?.padding || false

    const confirmMessage = intl.formatMessage({
        defaultMessage: 'Are you sure you want to leave? Your changes may not be saved.',
        id: 'component_dirty_form_confirm_may_not_be_saved',
    })

    const openDrawer = useCallback((content: ReactNode, options?: DrawerOptions) => {
        setDrawerContent(content)
        const { onClose, ...restOptions } = options || {}
        onCloseRef.current = onClose
        setDrawerOptions(restOptions)
        setIsOpen(true)
    }, [])

    const handleCloseDrawer = useCallback(() => {
        const shouldClose = canClose || window.confirm(confirmMessage) // eslint-disable-line no-alert

        if (shouldClose) {
            setIsOpen(false)
            setCanClose(true)
            onCloseRef.current?.()
            onCloseRef.current = undefined
        }
    }, [confirmMessage, canClose])

    const handleRouteChange = useCallback(() => {
        setIsOpen(false)
        setCanClose(true)
        onCloseRef.current = undefined
    }, [])

    useEffect(() => {
        router.events.on('routeChangeStart', handleRouteChange)

        return () => {
            router.events.off('routeChangeStart', handleRouteChange)
        }
    }, [router.events, handleRouteChange])

    const contextValue: DrawerProviderContext = useMemo(() => [
        openDrawer,
        handleCloseDrawer,
        setCanClose,
    ], [handleCloseDrawer, openDrawer])

    return (
        <DrawerContext.Provider value={contextValue}>
            <Drawer
                variant="temporary"
                anchor="right"
                open={isOpen}
                onClose={(): void => handleCloseDrawer()}
                sx={{
                    zIndex: 1300,
                    '& .MuiDrawer-paperAnchorRight': { width: widths[variant] },
                }}
            >
                <Box sx={{ p: padding ? 6 : 0 }}>
                    <ModalContextProvider>
                        <DisplayModeContext.Provider value="drawer">
                            {drawerContent}
                        </DisplayModeContext.Provider>
                    </ModalContextProvider>
                </Box>
            </Drawer>
            {children}
        </DrawerContext.Provider>
    )
}
